import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { MoroccanDivider } from "../components/MoroccanDivider";
import { site, essence, images } from "../data/site";
import { GallerySection } from "../components/Gallery";
import { useT } from "../data/i18n";
import { Seo } from "../components/Seo";

const Hero = () => {
  const { t } = useT();
  const bgRef = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = window.scrollY;
      bgRef.current.style.transform = `scale(1.08) translateY(${y * 0.15}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="hero" data-testid="hero-section">
      <div
        ref={bgRef}
        className="hero-bg"
        style={{ backgroundImage: `url(${images.realTagine})` }}
        aria-hidden="true"
      />
      <div className="hero-overlay" aria-hidden="true" />
      <div className="container">
        <div className="hero-content">
          <Reveal><div className="label">{t("hero_kicker")}</div></Reveal>
          <Reveal delay={1}>
            <h1>{t("hero_title_a")} <em>{t("hero_title_em")}</em> {t("hero_title_b")}</h1>
          </Reveal>
          <Reveal delay={2}><p className="lead">{t("hero_lead")}</p></Reveal>
          <Reveal delay={3}>
            <div className="hero-buttons">
              <Link to="/carta" className="btn primary" data-testid="hero-menu-btn">{t("btn_menu")}</Link>
              <a href={site.mapLink} target="_blank" rel="noreferrer" className="btn ghost" data-testid="hero-directions-btn">{t("btn_map")}</a>
            </div>
          </Reveal>
        </div>
      </div>
      <div className="hero-scroll" aria-hidden="true">↓</div>
    </section>
  );
};

const Impact = () => {
  const { t } = useT();
  return (
    <section className="impact" data-testid="impact-section">
      <div className="container">
        <Reveal>
          <h2>{t("impact_a")} <em>{t("impact_a_em")}</em>{t("impact_b")} <em>{t("impact_c_em")}</em>.</h2>
        </Reveal>
        <Reveal delay={1}><p>{t("impact_sub")}</p></Reveal>
      </div>
    </section>
  );
};

const Philosophy = () => {
  const { t } = useT();
  return (
    <section className="philosophy" data-testid="philosophy-section">
      <div className="container">
        <Reveal><div className="label">{t("philosophy_kicker")}</div></Reveal>
        <Reveal delay={1}>
          <h2 style={{ marginTop: 14, maxWidth: 900 }}>{t("philosophy_title")}</h2>
        </Reveal>
        <div className="cols">
          <Reveal delay={2}><p>{t("philosophy_p1")}</p></Reveal>
          <Reveal delay={3}><p>{t("philosophy_p2")}</p></Reveal>
        </div>
        <div className="divider" />
        <Reveal delay={2}>
          <div
            className="band"
            style={{ backgroundImage: `url(${images.spicesBand})` }}
            role="img"
            aria-label="Interior de Yasmina, barra y comedor con luz natural"
          />
        </Reveal>
      </div>
    </section>
  );
};

const Essence = () => {
  const { t } = useT();
  const items = [1, 2, 3, 4].map((n) => ({
    kicker: t(`ess${n}_kicker`), name: t(`ess${n}_name`), desc: t(`ess${n}_desc`),
  }));
  return (
    <section className="essence" data-testid="essence-section" style={{ background: "var(--bg-2)" }}>
      <div className="container">
        <Reveal><div className="label">{t("essence_kicker")}</div></Reveal>
        <Reveal delay={1}>
          <h2 style={{ marginTop: 14, maxWidth: 780 }}>{t("essence_title")}</h2>
        </Reveal>
        <div className="grid">
          {essence.map((d, i) => (
            <Reveal key={d.name} delay={Math.min(4, i + 1)}>
              <article className="dish-tile" data-testid={`dish-tile-${i}`}>
                <div
                  className="img"
                  style={{ backgroundImage: `url(${d.img})` }}
                  role="img"
                  aria-label={d.alt}
                />
                <div className="body">
                  <div className="kicker">{items[i].kicker}</div>
                  <h3>{items[i].name}</h3>
                  <p style={{ marginTop: 10 }}>{items[i].desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Tasting = () => {
  const { t } = useT();
  return (
    <section className="tasting" data-testid="tasting-section">
      <div className="container">
        <div className="row">
          <Reveal>
            <div
              className="img"
              style={{ backgroundImage: `url(${images.sharingTable})` }}
              role="img"
              aria-label="Comedor de Yasmina con mesas preparadas para compartir"
            />
          </Reveal>
          <div>
            <Reveal><div className="label">{t("tasting_kicker")}</div></Reveal>
            <Reveal delay={1}>
              <h2 style={{ marginTop: 14 }}>{t("tasting_title")}</h2>
            </Reveal>
            <Reveal delay={2}>
              <p style={{ marginTop: 22 }}>{t("tasting_desc")}</p>
            </Reveal>
            <Reveal delay={3}>
              <div style={{ display: "flex", gap: 12, marginTop: 26, flexWrap: "wrap" }}>
                <Link to="/carta" className="btn" data-testid="tasting-menu-btn">
                  {t("tasting_btn")}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div className="marquee" aria-hidden="true">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} style={{ display: "flex", gap: 60 }}>
          <span>Tagines <em>·</em></span>
          <span>Cuscús <em>·</em></span>
          <span>Harira <em>·</em></span>
          <span>Té moruno <em>·</em></span>
          <span>Ras el hanout <em>·</em></span>
          <span>Halal <em>·</em></span>
          <span>Puigcerdà <em>·</em></span>
        </div>
      ))}
    </div>
  </div>
);

const Reviews = () => {
  const { t } = useT();
  const cards = [1, 2, 3].map((n) => ({ title: t(`rev${n}_title`), text: t(`rev${n}_text`) }));
  return (
    <section className="reviews" data-testid="reviews-section">
      <div className="container">
        <Reveal><div className="label">{t("reviews_kicker")}</div></Reveal>
        <Reveal delay={1}>
          <h2 style={{ marginTop: 14, maxWidth: 900 }}>{t("reviews_title")}</h2>
        </Reveal>
        <div className="carousel">
          {cards.map((r, i) => (
            <Reveal key={r.title} delay={Math.min(3, i + 1)}>
              <div className="review-card" data-testid={`review-card-${i}`}>
                <p>{r.text}</p>
                <div className="who">
                  <div className="name">{r.title}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const { t } = useT();
  return (
    <section className="contact-block" data-testid="home-contact-section" style={{ background: "var(--bg-2)" }}>
      <div className="container">
        <Reveal><div className="label">{t("homecontact_kicker")}</div></Reveal>
        <Reveal delay={1}>
          <h2 style={{ marginTop: 14, maxWidth: 900 }}>{t("homecontact_title")}</h2>
        </Reveal>
        <Reveal delay={2}>
          <p style={{ marginTop: 18, maxWidth: 560 }}>{t("homecontact_desc")}</p>
        </Reveal>
        <div className="contact-row">
          <Reveal>
            <div className="map-wrap">
              <iframe
                title="Ubicación Yasmina Puigcerdà"
                src={site.mapEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="home-map-iframe"
              />
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="hours-panel">
              <h3>{t("homecontact_visit")}</h3>
              {site.hours.map((h) => (
                <div key={h.day} className="hours-row">
                  <span>{h.day}</span><span>{h.time}</span>
                </div>
              ))}
              <div style={{ opacity: 0.9, color: "var(--text)" }}>{site.address}</div>
              <div className="buttons">
                <a href={site.mapLink} target="_blank" rel="noreferrer" className="btn ghost" data-testid="contact-directions-btn">
                  {t("btn_map")}
                </a>
              </div>
              <p style={{ fontSize: 13 }}>{t("homecontact_note")}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Home = () => {
  const { t } = useT();
  return (
    <>
      <Seo title="Yasmina | Restaurante de Cocina Árabe en Puigcerdà" description="Restaurante de cocina árabe y marroquí halal en Puigcerdà. Tagines, cuscús y té moruno en la Plaça del Reg. Abierto cada día." path="/" />
      <Hero />
      <Impact />
      <MoroccanDivider />
      <Philosophy />
      <GallerySection t={t} />
      <MoroccanDivider />
      <Essence />
      <Tasting />
      <Marquee />
      <Reviews />
      <Contact />
    </>
  );
};
